import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  useMediaQuery,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  CircularProgress,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  deleteBusinessCase,
  exportBusinessCaseToExcel,
  getBusinessCases,
  type IBusinessCase,
} from "../../services/businessCaseService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import CustomTable, {
  type PaginationConfig,
  type TableColumn,
} from "../../components/customGenericComponents/CustomTable/CustomTable";
import "./Dashboard.css";
import theme from "../../theme";
import { useTranslation } from "react-i18next";
import type { TranslationKeys } from "../../i18n";
import { useBCT } from "../../hooks/useBCT";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const Dashboard: React.FC = () => {
  const { t } = useTranslation<TranslationKeys>();
  const [businessCases, setBusinessCases] = useState<IBusinessCase[]>([]);
  const [businessCasesLoading, setBusinessCasesLoading] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const isExportEnabled = import.meta.env.VITE_ENABLE_EXCEL_EXPORT === 'true';
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { businessUnits, businessUnitsError } =
    useBCT();
  
  // Get initial page size from localStorage or default to 10
  const getInitialPageSize = () => {
    const savedPageSize = localStorage.getItem('dashboardPageSize');
    return savedPageSize ? parseInt(savedPageSize, 10) : 10;
  };

  const [query, setQuery] = useState({
    search: "",
    sortField: "created_at",
    sortOrder: "desc" as "asc" | "desc",
    filters: {} as Record<string, string>,
    page: 1,
    pageSize: getInitialPageSize(),
  });

  const [totalRows, setTotalRows] = useState(0);

  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    businessCase: IBusinessCase | null;
  }>({
    open: false,
    businessCase: null,
  });
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (businessCase: IBusinessCase) => {
    setDeleteModal({
      open: true,
      businessCase,
    });
  };

  const handleExportClick = async (businessCase: IBusinessCase) => {
    try {
      setExporting(businessCase.id);
      toast.info(t("export_started"));

      const blob = await exportBusinessCaseToExcel(businessCase.id);
      
      const safeProjectName = businessCase.project_name.replace(/[^a-z0-9]/gi, '_');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `${safeProjectName}_BusinessCase_${timestamp}.xlsx`;
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t("export_successful"));
    } catch (error: any) {
      console.error("Error exporting business case:", error);
      toast.error(error?.response?.data?.error || t("export_failed"));
    } finally {
      setExporting(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.businessCase) return;

    try {
      setDeleting(true);

      const result = await deleteBusinessCase(deleteModal.businessCase.id);

      toast.success(result.message);

      // Refresh the business cases list
      loadBusinessCases();

      // Close modal
      setDeleteModal({ open: false, businessCase: null });
      // refreshSharepointFiles().catch(err =>
      //   console.error("Failed to refresh SharePoint files:", err)
      // );
    } catch (error: any) {
      console.error("Error deleting business case:", error);

      if (error?.response?.status === 404) {
        toast.error(
          error.message || "Business case not found or already deleted."
        );
        // Refresh list to remove from UI if it was already deleted
        loadBusinessCases();
      } else {
        toast.error(
          error?.response?.data?.error || "Failed to delete business case."
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({ open: false, businessCase: null });
  };

  useEffect(() => {
    // No need to fetch here; it's handled by context
    if (businessUnitsError) {
      setError(businessUnitsError);
    }
  }, [businessUnitsError]);

  const loadBusinessCases = async () => {
    try {
      setBusinessCasesLoading(true);
      // const response = await getBusinessCases(query);
      const response = await getBusinessCases({
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        sortOrder: query.sortOrder,
        sortField: query.sortField,
        ...query.filters,
      });
      setBusinessCases(response.data);
      setTotalRows(response.total);
      setError(null);
    } catch (error) {
      console.error("Failed to load business cases:", error);
      setError("Failed to load business cases");
      toast.error(t("load_business_cases_failed"));
    } finally {
      setBusinessCasesLoading(false);
    }
  };

  useEffect(() => {
    loadBusinessCases();
  }, [query]);

  // Listen for live updates from background tasks
  useEffect(() => {
    const handleBusinessCaseCreated = () => {
      loadBusinessCases();
    };

    const handleBusinessCaseUpdated = () => {
      loadBusinessCases();
    };

    window.addEventListener('business-case-created', handleBusinessCaseCreated);
    window.addEventListener('business-case-updated', handleBusinessCaseUpdated);

    return () => {
      window.removeEventListener('business-case-created', handleBusinessCaseCreated);
      window.removeEventListener('business-case-updated', handleBusinessCaseUpdated);
    };
  }, [query]); // Re-subscribe when query changes

  const handleCreateBusinessCase = () => {
    navigate("/create-business-case");
  };

  const handleViewBusinessCase = (id: string) => {
    navigate(`/business-case/${id}`);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: TableColumn[] = [
    {
      id: "bc_name",
      label: t("column.bc_name"),
      isSearchable: true,
      hasCalendar: false,
      isSortable: true,
      colType: "text",
      filterable: false,
      minWidth: isMobile ? 140 : 180,
      format: (value: string, _row: IBusinessCase) => (
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: "clamp(0.75rem, 1.25vw, 0.875rem)",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "project_name",
      label: t("column.project_name"),
      isSearchable: true,
      hasCalendar: false,
      isSortable: true,
      colType: "text",
      filterable: false,
      minWidth: isMobile ? 120 : 150,
      format: (value: string, _row: IBusinessCase) => (
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: "clamp(0.75rem, 1.25vw, 0.875rem)",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "bc_owner",
      label: t("column.bc_owner"),
      minWidth: isMobile ? 100 : 130,
      isSearchable: true,
      hasCalendar: false,
      colType: "text",
      filterable: false,
      isSortable: true,
      hideOnMobile: isMobile,
      format: (value: string) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "clamp(0.75rem, 1.25vw, 0.875rem)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value || "N/A"}
        </Typography>
      ),
    },
    {
      id: "gpm",
      label: t("column.gpm"),
      minWidth: isMobile ? 80 : 100,
      isSearchable: true,
      hideOnMobile: isMobile,
      hasCalendar: false,
      colType: "text",
      filterable: false,
      isSortable: true,
      format: (value: string) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "clamp(0.75rem, 1.25vw, 0.875rem)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value || "N/A"}
        </Typography>
      ),
    },
    {
      id: "bu_name",
      label: t("column.business_unit"),
      isSearchable: true,
      hasCalendar: false,
      isSortable: true,
      filterable: true,
      colType: "text",
      minWidth: isMobile ? 120 : 140,
      hideOnMobile: isTablet,
      format: (value: string) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "clamp(0.75rem, 1.25vw, 0.875rem)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value || "N/A"}
        </Typography>
      ),
    },
    {
      id: "cpr_date",
      label: t("column.cpr_date"),
      isSearchable: false,
      hasCalendar: true,
      isSortable: true,
      colType: "date",
      filterable: false,
      minWidth: isMobile ? 90 : 110,
      hideOnMobile: isMobile,
      format: (value: string) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "clamp(0.675rem, 1.25vw, 0.75rem)",
            whiteSpace: "nowrap",
          }}
        >
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      id: "created_info",
      label: t("column.created_at"),
      isSearchable: false,
      hasCalendar: false,
      isSortable: false,
      colType: "text",
      filterable: false,
      minWidth: isMobile ? 110 : 140,
      hideOnMobile: isTablet,
      format: (_value: any, row: IBusinessCase) => (
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: "clamp(0.675rem, 1.25vw, 0.75rem)",
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            {formatDateTime(row.created_at)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "clamp(0.625rem, 1vw, 0.65rem)",
              color: "text.secondary",
              lineHeight: 1,
            }}
          >
            by {row.created_by_name || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "updated_info",
      label: t("column.updated_at"),
      isSearchable: false,
      hasCalendar: false,
      isSortable: true,
      colType: "text",
      filterable: false,
      minWidth: isMobile ? 110 : 140,
      hideOnMobile: isTablet,
      format: (_value: any, row: IBusinessCase) => (
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: "clamp(0.675rem, 1.25vw, 0.75rem)",
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            {formatDateTime(row.last_updated_at)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "clamp(0.625rem, 1vw, 0.65rem)",
              color: "text.secondary",
              lineHeight: 1,
            }}
          >
            by {row.last_updated_by_name || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "actions",
      label: t("column.actions"),
      minWidth: isMobile ? 80 : 100,
      isSearchable: false,
      hasCalendar: false,
      isSortable: false,
      filterable: false,
      align: "center",
      sticky: true,
      format: (_value: any, row: IBusinessCase) => (
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? 0.5 : 1,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Tooltip title="View/Edit">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleViewBusinessCase(row.id);
              }}
              color="primary"
              sx={{
                p: isMobile ? 0.5 : 1,
                minWidth: "auto",
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isExportEnabled && (
            <Tooltip title={t("export_to_excel")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleExportClick(row);
                }}
                color="primary"
                disabled={exporting === row.id}
                sx={{
                  p: isMobile ? 0.5 : 1,
                  minWidth: "auto",
                }}
              >
                {exporting === row.id ? (
                  <CircularProgress size={20} />
                ) : (
                  <FileDownloadIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row);
              }}
              sx={{
                p: isMobile ? 0.5 : 1,
                minWidth: "auto",
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const DeleteConfirmationModal: React.FC = () => (
    <Dialog
      open={deleteModal.open}
      onClose={handleCancelDelete}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            {t("confirm_delete_title")}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCancelDelete}
            disabled={deleting}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t("confirm_delete")}
        </Typography>
        {deleteModal.businessCase && (
          <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("project_name")} :
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              {deleteModal.businessCase.project_name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {t("business_case_owner")}
            </Typography>
            <Typography variant="body1">
              {deleteModal.businessCase.bc_owner || "N/A"}
            </Typography>
          </Box>
        )}
        <Typography
          variant="body2"
          color="warning.main"
          sx={{ mt: 2, fontStyle: "italic" }}
        >
          {t("delete_warning")}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancelDelete}
          disabled={deleting}
          color="primary"
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleConfirmDelete}
          disabled={deleting}
          variant="contained"
          color="error"
          startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {deleting ? t("deleting") : t("delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const paginationConfig: PaginationConfig = {
    enabled: true,
    page: query.page,
    pageSize: query.pageSize,
    pageSizeOptions: isMobile ? [5, 10, 15] : [5, 10, 25, 50],
    showPageSizeSelector: true,
    total: totalRows,
    onPageChange: (newPage: number) =>
      setQuery((prev) => ({ ...prev, page: newPage })),
    onPageSizeChange: (newPageSize: number) => {
      localStorage.setItem('dashboardPageSize', newPageSize.toString());
      setQuery((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
    },
  };

  return (
    <Box className="dashboard-container" overflow={"auto"}>
      <Box className="dashboard-header">
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            fontSize="medium"
            component="button"
            variant="body2"
            onClick={() => navigate("/")}
            sx={{ textDecoration: "none", color: "text.secondary" }}
          >
            {t("dashboard")}
          </Link>
        </Breadcrumbs>
        <Button
          variant="contained"
          onClick={handleCreateBusinessCase}
          size={isMobile ? "small" : "medium"}
          className="create-button"
          startIcon={<AddIcon />}
          sx={{
            textTransform: "none",
            padding: isMobile ? "6px 8px" : "6px 8px",
          }}
        >
          {isMobile ? t("create") : t("create_business_case")}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" className="dashboard-error">
          {error}
        </Alert>
      )}

      <CustomTable
        columns={columns}
        businessUnits={businessUnits || []}
        data={businessCases}
        loading={businessCasesLoading}
        pagination={{
          ...paginationConfig,
          total: totalRows,
          page: query.page,
          pageSize: query.pageSize,
          onPageChange: (newPage: number) =>
            setQuery((prev) => ({ ...prev, page: newPage })),
          onPageSizeChange: (newPageSize: number) => {
            localStorage.setItem('dashboardPageSize', newPageSize.toString());
            setQuery((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
          },
        }}
        onSearch={(searchValue: string) =>
          setQuery((prev) => ({ ...prev, search: searchValue, page: 1 }))
        }
        onSort={(sortField: string, sortOrder: "asc" | "desc") =>
          setQuery((prev) => ({ ...prev, sortField, sortOrder, page: 1 }))
        }
        onFilter={(filters: Record<string, string>) =>
          setQuery((prev) => ({ ...prev, filters, page: 1 }))
        }
        emptyMessage={t("no_business_cases")}
      />
      <DeleteConfirmationModal />
    </Box>
  );
};

export default Dashboard;
